import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, CardMedia, Typography, Button, Grid, Skeleton, useTheme } from '@mui/material';
import axios from 'axios';
import { getFullApiUrl } from '../utils/api';

export {};

interface Advertisement {
  id: number;
  _id?: string; // Keep for backward compatibility
  title: string;
  description: string;
  imageUrl?: string;
  linkUrl?: string;
  buttonText: string;
  position: string;
  isActive: boolean;
  priority: number;
  impressions: number;
  clicks: number;
}

interface DynamicAdvertisementProps {
  position: string;
}

const DynamicAdvertisement: React.FC<DynamicAdvertisementProps> = ({ position }) => {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const fetchAdvertisements = React.useCallback(async () => {
    try {
      console.log(`[DynamicAdvertisement] Fetching ads for position: ${position}`);
      setLoading(true);
      const response = await axios.get(getFullApiUrl(`/api/advertisements/active/${position}`));
      console.log(`[DynamicAdvertisement] Response for ${position}:`, response.data);
      setAdvertisements(response.data as Advertisement[]);
    } catch (error) {
      console.error(`[DynamicAdvertisement] Error fetching advertisements for ${position}:`, error);
      setAdvertisements([]);
    } finally {
      setLoading(false);
    }
  }, [position]);

  useEffect(() => {
    fetchAdvertisements();
  }, [position, fetchAdvertisements]);

  const handleAdClick = async (ad: Advertisement) => {
    try {
      // Track the click
      await axios.post(getFullApiUrl(`/api/advertisements/${ad.id}/click`));

      // Open the link if exists
      if (ad.linkUrl) {
        // Check if linkUrl starts with http/https, if not add it
        let url = ad.linkUrl;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'http://' + url;
        }
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error tracking ad click:', error);
      // Still open the link even if tracking fails
      if (ad.linkUrl) {
        let url = ad.linkUrl;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'http://' + url;
        }
        window.open(url, '_blank');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card sx={{ height: 200 }}>
                <Skeleton variant="rectangular" height={140} />
                <CardContent>
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={20} width="60%" />
                  <Skeleton variant="rectangular" height={36} width={120} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (advertisements.length === 0) {
    console.log(`[DynamicAdvertisement] No ads found for position: ${position}, showing default ad`);
    return (
      <Grid item xs={12} md={6} key={`default-${position}`}>
        <Card
          sx={{
            height: '100%',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[8]
            }
          }}
          onClick={() => { window.location.href = '/contact#ticket-form'; }}
        >
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              جایگاه تبلیغ شما
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
              برای تبلیغات در این جایگاه با ما تماس بگیرید
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{
                alignSelf: 'center',
                backgroundColor: theme.palette.secondary.main,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.dark
                }
              }}
              onClick={() => { window.location.href = '/contact#ticket-form'; }}
            >
              ارتباط با ما
            </Button>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} md={6}>
      {advertisements.length === 1 && advertisements.map((ad) => (
        <Card
          key={ad.id}
          sx={{
            height: '100%',
            cursor: ad.linkUrl ? 'pointer' : 'default',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[8]
            }
          }}
          onClick={() => ad.linkUrl && handleAdClick(ad)}
        >
          {ad.imageUrl && (
            <CardMedia
              component="img"
              height="140"
              image={getFullApiUrl(ad.imageUrl || '')}
              alt={ad.title}
              sx={{ objectFit: 'cover' }}
            />
          )}
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              {ad.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
              {ad.description}
            </Typography>
            {ad.linkUrl && (
              <Button
                variant="contained"
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: theme.palette.secondary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark
                  }
                }}
              >
                {ad.buttonText}
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </Grid>
  );
};

export default DynamicAdvertisement;
